import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import arraySort from 'array-sort'
import update from 'immutability-helper'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import styled from 'styled-components'
import withApiData from '../../HOC/withApiData'
import colors from '../../../configuration/colors'
import InputLabel from '../../typography/Input/InputLabel'
import H5 from '../../typography/headers/H5'
import TextInputComponent from '../../modal/CreateOrEditCourseModal/InputComponents/TextInputComponent'
import MarginRight15 from '../../typography/margins/MarginRight15'
import CancelButton from '../../mini/Buttons/CancelButton'
import Button from '../../mini/Buttons/Button'
import SelectorComponent from '../../modal/CreateOrEditCourseModal/InputComponents/SelectorComponent'
import { createResource, addLinkedSkill, deleteLinkedSkill, editLevelActivity } from './API'
import { getTheActivityType } from './getTheActivityTypeFunction'
import ErrorMessage from '../../auth/ErrorMessage'
import { showModal } from '../../../actions/modal'
import MentorFileRow from './MentorFileRow'
import ResourceRow from './ResourceRow'
import { simpleMDEOptions } from '../../../utils/markdowneditor'
import EditLinkedSkills from './EditLinkedSkills'

const EditorButtonsWrapper = styled.div`
  display: flex;
  align-content: center;
  align-items: flex-start;
  justify-content: flex-end;
  ${props => (props.top ? `
    position: absolute;
    top: 62px;
    right: 0;
  ` : `
    margin-top: 30px;
  `)};
`

const activityTypeOptions = [
  {
    id: 1,
    name: 'Artifact Required / Check-in',
    label: 'Artifact Required / Check-in',
    sort_order: 0
  },
  { id: 2, name: 'Check-in Only', label: 'Check-in Only', sort_order: 1 },
  { id: 3, name: 'No Check-in', label: 'No Check-in', sort_order: 2 }
]

const EditorButtons = ({
  handleCloseEditMode,
  handleSaveEditedActivity,
  errorQuantity,
  top
}) => (
  <EditorButtonsWrapper top={top}>
    <MarginRight15>
      <CancelButton
        handleClick={handleCloseEditMode}
        title="Cancel"
        width="104px"
      />
    </MarginRight15>
    <div>
      <Button
        width="125px"
        title="Save Draft"
        handleClick={handleSaveEditedActivity}
        buttonColor={colors.oeYellow}
        borderColor={colors.oeYellow}
        hoverBackground={colors.charcoal4}
        hoverColor={colors.white}
        couldSaveData={errorQuantity === 0}
        shouldShowTooltipAndDisableButton={true}
      />
      {errorQuantity > 0 && (
        <ErrorMessage>Oops! {errorQuantity} errors found.</ErrorMessage>
      )}
    </div>
  </EditorButtonsWrapper>
)

const EditActivityDetails = ({
  apiData,
  activity,
  mentorFiles = [],
  resources = [],
  linkedSkills: passedLinkedSkills = [],
  levelId,
  handleCloseEditMode
}) => {
  const dispatch = useDispatch()

  const queryClient = useQueryClient()
  const artifactTypes = useSelector(state => state.artifacttypes.items)
  const artifactType =
    !!activity && artifactTypes.find(at => at.id === activity.artifact_type_id)

  const [activityNameState, setActivityName] = useState(activity.name)
  const handleActivityName = e => {
    setActivityName(e.target.value)
  }

  const [selectedActivityType, setSelectedActivityType] = useState(
    getTheActivityType(activity)
  )
  const handleSelectedActivityType = e => {
    if (e.id !== 1) {
      setArtifactDescription(null)
      setSelectedArtifactType(null)
    }
    setSelectedActivityType(e)
  }

  const [selectedArtifactType, setSelectedArtifactType] = useState(
    activity.artifact_type_id !== null ? artifactType : null
  )
  const handleSelectedArtifactType = e => {
    setSelectedArtifactType(e)
  }

  const [artifactDescription, setArtifactDescription] = useState(
    activity.artifact_description !== null
      ? activity.artifact_description
      : null
  )
  const handleArtifactDescription = e => {
    setArtifactDescription(e.target.value)
  }

  const [drivingQuestion, setDrivingQuestion] = useState(
    activity.driving_question !== null ? activity.driving_question : null
  )
  const handleDrivingQuestion = e => {
    setDrivingQuestion(e.target.value)
  }

  const [activityGoals, setActivityGoals] = useState(
    activity.goals !== null ? activity.goals : null
  )
  const handleActivityGoals = e => {
    setActivityGoals(e.target.value)
  }

  const [linkedSkills, setLinkedSkills] = useState(passedLinkedSkills)

  const [activityDescription, setActivityDescription] = useState(
    activity.description !== null ? activity.description : null
  )
  const handleActivityDescription = e => {
    let event = {
      target: {}
    }
    event.target.id = 'activity-details'
    event.target.value = e

    setActivityDescription(event.target.value)
  }

  const [mentorNotes, setMentorNotes] = useState(
    activity.mentor_notes !== null ? activity.mentor_notes : null
  )
  const handleMentorNotes = e => {
    let event = {
      target: {}
    }
    event.target.id = 'activity-details'
    event.target.value = e

    setMentorNotes(event.target.value)
  }

  const [mentorAssessmentQuestions, setMentorAssessmentQuestions] = useState(
    activity.mentor_assessment_questions !== null
      ? activity.mentor_assessment_questions
      : null
  )
  const handleMentorAssessmentQuestions = e => {
    let event = {
      target: {}
    }
    event.target.id = 'activity-details'
    event.target.value = e

    setMentorAssessmentQuestions(event.target.value)
  }

  const editActivityMutation = useMutation(
    ({ apiData, levelId, updatedActivityData }) =>
      editLevelActivity(apiData, levelId, updatedActivityData)
  )

  // saves linked skills and updates the ordering of the skills if needed
  const saveLinkedSkills = () => new Promise(resolve => {
    const toDelete = []
    const toAdd = []
    // traverse skills backward
    let i = passedLinkedSkills.length - 1
    let j = linkedSkills.length - 1
    // always step through the old list (which is what's currently on the BE)
    for ( ; i >= 0 && j >= 0; i--) {
      if (passedLinkedSkills[i].framework_skill_id ===
          linkedSkills[j].framework_skill_id) {
        // there's no discrepancy here, we can step through the new list as well
        j--
      } else {
        // there is a discrepancy, this means that either
        // passedLinkedSkills[i] was deleted OR
        // it was moved higher in the ordering, and we have to delete and re-add later
        toDelete.push(passedLinkedSkills[i])
      }
    }
    // if we have leftover in passedLinkedSkills, that means we already matched
    // everything in linkedSkills, so what's left isn't there and must be deleted
    for ( ; i >= 0; i--) toDelete.push(passedLinkedSkills[i])
    // anything leftover in linkedSkills wasn't matched in passedLinkedSkills,
    // either because it didn't exist or it was in the wrong spot and got added
    // to toDelete. Either way we need to add it
    for ( ; j >= 0; j--) toAdd.push(linkedSkills[j])

    // delete, make a promise to wait for all deletions to finish
    const deletionPromise = Promise.all(toDelete.map(
      s => deleteLinkedSkill(apiData, levelId, activity.id, s.framework_skill_id)
    ))
    // add sequentially, waiting for previous add (or deletion) to finish
     toAdd.reduce((prev, curr) => // prev is a promise, curr is a skill
      prev.then(() => // chain the promises
        addLinkedSkill(apiData, levelId, activity.id, curr.framework_skill_id)
      ),
      deletionPromise // initial thing to wait for is all the deletions
    ).then(
      // invalidate queries only after all modifications are done
      () => queryClient.invalidateQueries('linked-skills-' + activity.id)
    ).then(
      () => resolve()
    )
  })

  const handleSaveEditedActivity = () => {
    const linkedSkillsPromise = saveLinkedSkills()

    const updatedActivityData = {
      id: activity.id,
      name: activityNameState,
      description: activityDescription,
      driving_question: drivingQuestion,
      goals: activityGoals,
      artifact_description:
        selectedActivityType === null
          ? null
          : selectedActivityType.id !== 1 && artifactDescription === null
          ? null
          : artifactDescription,
      artifact_type_id:
        selectedActivityType === null
          ? null
          : selectedArtifactType === null
          ? null
          : selectedArtifactType.id,
      checkpoint:
        selectedActivityType !== null
          ? selectedActivityType.label === 'Check-in Only' ||
            selectedActivityType.label === 'Artifact Required / Check-in'
          : false,
      work_product:
        selectedActivityType !== null
          ? selectedActivityType.label === 'Artifact Required / Check-in'
          : false,
      mentor_notes: mentorNotes,
      mentor_assessment_questions: mentorAssessmentQuestions
    }

    const activityEditPromise = new Promise(resolve =>
      editActivityMutation.mutate(
        {
          apiData,
          levelId,
          updatedActivityData
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('activity-' + activity.id)
            queryClient.invalidateQueries('mentorFiles-' + activity.id)
            queryClient.invalidateQueries('resources-' + activity.id)

            resolve()
          }
        }
      )
    )

    Promise.all([activityEditPromise, linkedSkillsPromise])
      .then(() => handleCloseEditMode())
  }

  const [mentorFilesState, setMentorFilesState] = useState(mentorFiles)
  useEffect(() => {
    if (mentorFilesState !== mentorFiles) {
      setMentorFilesState(arraySort(mentorFiles, 'sort_order'))
    }
  }, [mentorFiles])

  const moveMentorFileCard = useCallback(
    (dragIndex, hoverIndex) => {
      const mentorFilesStateCopy = [...mentorFilesState]
      const dragCard = mentorFilesStateCopy[dragIndex]
      setMentorFilesState(
        update(mentorFilesStateCopy, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard]
          ]
        })
      )
    },
    [mentorFilesState]
  )

  const listOfMentorFiles = mentorFilesState.map((mentorFile, index) => {
    return (
      <MentorFileRow
        activityQuestId={activity.id}
        levelId={levelId}
        mentorFile={mentorFile}
        index={index}
        moveCard={moveMentorFileCard}
        id={mentorFile.id}
        isEditActivity={true}
      />
    )
  })

  const [resourcesState, setResourcesState] = useState(resources)
  useEffect(() => {
    if (resourcesState !== resources) {
      setResourcesState(arraySort(resources, 'sort_order'))
    }
  }, [resources])

  const moveResourcesCard = useCallback(
    (dragIndex, hoverIndex) => {
      const resourcesStateCopy = [...resourcesState]
      const dragCard = resourcesStateCopy[dragIndex]

      setResourcesState(
        update(resourcesStateCopy, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard]
          ]
        })
      )
    },
    [resourcesState]
  )

  const listOfResources = resourcesState.map((resource, index) => {
    return (
      <ResourceRow
        activityQuestId={activity.id}
        levelId={levelId}
        resource={resource}
        index={index}
        moveCard={moveResourcesCard}
        id={resource.id}
      />
    )
  })

  const [errorQuantity, setErrorQuantity] = useState(0)

  useEffect(() => {
    const errors = [
      !activityNameState,
      drivingQuestion && drivingQuestion.length > 255,
      activityGoals && activityGoals.length > 255
    ]
    const count = errors.filter(b => b).length
    setErrorQuantity(count)
  })

  const markdownOptions = (type, value) => {
    const descriptionOptions = Object.assign({}, simpleMDEOptions)
    descriptionOptions.initialValue = value
    descriptionOptions.placeholder = type
    descriptionOptions.autofocus = false
    return descriptionOptions
  }

  const createMutation = useMutation(
    ({ apiData, levelId, activityQuestId, resourceName }) =>
      createResource(apiData, levelId, activityQuestId, resourceName)
  )
  const handleCreateResourceAndShowModal = () => {
    createMutation.mutate(
      {
        apiData,
        levelId,
        activityQuestId: activity.id,
        resourceName: `Resource ${resources.length + 1}`
      },
      {
        onSuccess: data => {
          dispatch(
            showModal('ADD_RESOURCE_MODAL', {
              levelId,
              activityQuestId: activity.id,
              resource: data,
              resourceName: `Resource ${resources.length + 1}`,
              resourceId: data.id
            })
          )
        }
      }
    )
  }

  return (
    <div>
      <div style={{ width: '50%', marginTop: '-20px' }}>
        <TextInputComponent
          title="Activity Name"
          placeholder="Activity Name"
          value={activityNameState}
          handleEnteredTextValue={handleActivityName}
          shouldDisplayAsteriskSymbol={true}
          textColor={colors.victoria}
          isError={!activityNameState}
          errorMessage={'Name can’t be blank.'}
        />
        <SelectorComponent
          placeholder="Activity Type"
          handleSelectedValueChange={handleSelectedActivityType}
          selectedValue={selectedActivityType}
          options={activityTypeOptions}
          shouldDisplayAsterisk={true}
          textColor={colors.victoria}
        />
        {selectedActivityType !== null && selectedActivityType.id === 1 && (
          <SelectorComponent
            placeholder="Artifact Type"
            handleSelectedValueChange={handleSelectedArtifactType}
            selectedValue={selectedArtifactType}
            options={artifactTypes}
            shouldDisplayAsterisk={true}
            textColor={colors.victoria}
          />
        )}
      </div>
      {selectedActivityType !== null && selectedActivityType.id === 1 && (
        <TextInputComponent
          title="Artifact Description"
          placeholder="Artifact Description"
          hintText="The artifact description is a short,
          succinct sentence or phrase that clearly describes
          the artifact students will create to demonstrate
          their knowledge and skills. It should not instruct students,
          but rather act as a point of reference."
          handleEnteredTextValue={handleArtifactDescription}
          value={artifactDescription}
          shouldDisplayAsteriskSymbol={true}
          textColor={colors.victoria}
        />
      )}
      <TextInputComponent
        title="Driving Question"
        placeholder="Driving Question"
        hintText="An open-ended question that frames the activity that students are about to explore, collaborate, and reflect on."
        handleEnteredTextValue={handleDrivingQuestion}
        value={drivingQuestion}
        shouldDisplayAsteriskSymbol={true}
        textColor={colors.victoria}
        isError={drivingQuestion !== null && drivingQuestion.length > 255}
        errorMessage={'Maximum 255 characters.'}
      />
      <TextInputComponent
        title="Activity Goals"
        placeholder="Activity Goals"
        hintText="A brief statement written for students that clearly expresses an activity’s purpose and drives the rest of the activity."
        handleEnteredTextValue={handleActivityGoals}
        value={activityGoals}
        shouldDisplayAsteriskSymbol={true}
        textColor={colors.victoria}
        isError={activityGoals !== null && activityGoals.length > 255}
        errorMessage={'Maximum 255 characters.'}
      />
      <EditLinkedSkills
        linkedSkills={linkedSkills}
        setLinkedSkills={setLinkedSkills}
      />
      <TextInputComponent
        title="Activity Description"
        placeholder="Activity Description"
        hintText={`Provide information for students to guide their exploration and help them complete the activity.
        This description should not do teaching or give away key takeaways`}
        handleEnteredTextValue={handleActivityDescription}
        value={activityDescription}
        shouldDisplayAsteriskSymbol={true}
        shouldBeTextarea={true}
        textColor={colors.victoria}
        options={markdownOptions('Activity Description', activityDescription)}
      />
      <div>
        <InputLabel>Activity Resources</InputLabel>
        <Button
          handleClick={handleCreateResourceAndShowModal}
          small
          title="+ Add Resource"
          buttonColor={colors.siteBackground}
          borderColor={colors.rock}
          hoverBackground={colors.charcoal4}
          hoverColor={colors.white}
        />
        <DndProvider backend={HTML5Backend}>{listOfResources}</DndProvider>
      </div>
      <div
        style={{
          border: `1px ${colors.rock} solid`,
          borderRadius: '5px',
          padding: '18px',
          marginTop: '15px'
        }}
      >
        <H5 dove>Mentor Prep</H5>
        <div style={{ marginTop: '-6px' }}>
          <TextInputComponent
            title="Mentor Notes (Optional)"
            placeholder="Mentor Notes"
            handleEnteredTextValue={handleMentorNotes}
            value={mentorNotes}
            shouldDisplayAsteriskSymbol={false}
            shouldBeTextarea={true}
            textColor={colors.victoria}
            options={markdownOptions('Mentor Notes', mentorNotes)}
          />
        </div>
        <InputLabel>Mentor Files (Optional)</InputLabel>
        <Button
          handleClick={() =>
            dispatch(
              showModal('ADD_OR_EDIT_MENTOR_FILE', {
                levelId,
                activityQuestId: activity.id,
                isEditing: false
              })
            )
          }
          small
          title="+ Add File"
          buttonColor={colors.siteBackground}
          borderColor={colors.rock}
          hoverBackground={colors.charcoal4}
          hoverColor={colors.white}
        />
        <DndProvider backend={HTML5Backend}>{listOfMentorFiles}</DndProvider>
        <TextInputComponent
          title="Assessment Questions (Optional)"
          placeholder="Assessment Questions"
          handleEnteredTextValue={handleMentorAssessmentQuestions}
          value={mentorAssessmentQuestions}
          shouldDisplayAsteriskSymbol={false}
          shouldBeTextarea={true}
          textColor={colors.victoria}
          options={markdownOptions(
            'Assessment Questions',
            mentorAssessmentQuestions
          )}
        />
      </div>
      {/* Bottom of page editor buttons */}
      <EditorButtons
        handleCloseEditMode={handleCloseEditMode}
        handleSaveEditedActivity={handleSaveEditedActivity}
        errorQuantity={errorQuantity}
      />
      {/* Top of page editor buttons */}
      <EditorButtons
        handleCloseEditMode={handleCloseEditMode}
        handleSaveEditedActivity={handleSaveEditedActivity}
        errorQuantity={errorQuantity}
        top
      />
    </div>
  )
}

EditActivityDetails.propTypes = {
  apiData: PropTypes.object.isRequired,
  activity: PropTypes.object.isRequired,
  mentorFiles: PropTypes.array.isRequired,
  handleCloseEditMode: PropTypes.func,
  levelId: PropTypes.number.isRequired
}

export default withApiData(EditActivityDetails)



// WEBPACK FOOTER //
// ./src/components/mycourses/Activities/EditActivityDetails.js